<template>
  <div class="project-management">
    <div class="page-header">
      <h2 class="page-title">{{ tv('pageTitle') }}</h2>
    </div>

    <a-result
      v-if="pageError"
      status="error"
      :title="tv('loadFailed')"
    >
      <template #extra>
        <a-button type="primary" @click="refreshProjects">{{ t('common.retry') }}</a-button>
      </template>
    </a-result>

    <template v-else>
      <a-card
        v-if="!loading && storeProjects.length === 0"
        class="guide-card"
        :bordered="false"
      >
        <div class="guide-content">
          <div class="guide-text">
            <h3>{{ tv('emptyTitle') }}</h3>
            <p>{{ tv('emptyDesc') }}</p>
            <a-space>
              <a-button type="primary" @click="goToVideoUpload">
                <template #icon><VideoCameraOutlined /></template>
                {{ tv('uploadVideo') }}
              </a-button>
              <a-button @click="goToDocumentUpload">
                <template #icon><FileTextOutlined /></template>
                {{ tv('uploadDocument') }}
              </a-button>
            </a-space>
          </div>
        </div>
      </a-card>

      <a-card class="filter-card" :bordered="false">
        <div class="filter-content">
          <a-form layout="inline" @finish="handleSearch" class="filter-form">
            <a-form-item :label="t('common.dateRange')">
              <a-range-picker
                v-model:value="dateRange"
                style="width: 240px"
                :placeholder="[t('common.startDate'), t('common.endDate')]"
              />
            </a-form-item>
            <a-form-item>
              <a-input
                v-model:value="searchKeyword"
                :placeholder="tv('searchPlaceholder')"
                style="width: 200px"
                @pressEnter="handleSearch"
              >
                <template #prefix><SearchOutlined /></template>
              </a-input>
            </a-form-item>
            <a-form-item>
              <a-space>
                <a-button type="primary" html-type="submit" :loading="loading">
                  {{ t('common.search') }}
                </a-button>
                <a-button @click="resetFilters">{{ t('common.reset') }}</a-button>
              </a-space>
            </a-form-item>
          </a-form>
          <a-button :loading="loading" @click="refreshProjects">
            <template #icon><ReloadOutlined /></template>
            {{ t('common.refresh') }}
          </a-button>
        </div>
      </a-card>

      <div v-if="selectedRowKeys.length > 0" class="batch-actions">
        <a-card :bordered="false">
          <div class="batch-content">
            <span class="selected-info">
              {{ tv('selectedCount', { count: selectedRowKeys.length }) }}
            </span>
            <a-space>
              <a-button
                danger
                :loading="batchLoading"
                @click="batchDelete"
              >
                {{ t('common.batchDelete') }}
              </a-button>
              <a-button @click="clearSelection">{{ t('common.cancel') }}</a-button>
            </a-space>
          </div>
        </a-card>
      </div>

      <a-card :bordered="false">
        <a-table
          :columns="columns"
          :data-source="filteredProjects"
          :loading="loading"
          :pagination="paginationConfig"
          :row-selection="rowSelection"
          :custom-row="customRow"
          :locale="{ emptyText: tv('tableEmpty') }"
          :scroll="{ x: 960 }"
          row-key="id"
        >
          <template #bodyCell="{ column, record }">
            <template v-if="column.key === 'title'">
              <a-button type="link" @click.stop="goToDetail(record)">
                {{ record.title }}
              </a-button>
            </template>

            <template v-else-if="column.key === 'aggregate'">
              <span v-if="aggregatesLoading && !aggregates[record.id]">
                {{ tv('aggregateLoading') }}
              </span>
              <span v-else>{{ formatAggregate(record.id) }}</span>
            </template>

            <template v-else-if="column.key === 'creator'">
              {{ creatorLabel(record) }}
            </template>

            <template v-else-if="column.key === 'createdAt'">
              {{ formatDate(record.created_at) }}
            </template>

            <template v-else-if="column.key === 'action'">
              <a-space @click.stop>
                <a-button type="link" size="small" @click="goToDetail(record)">
                  {{ tv('openDetail') }}
                </a-button>
                <a-button
                  v-if="canDeleteProject(record)"
                  type="link"
                  size="small"
                  danger
                  @click="deleteProject(record)"
                >
                  {{ t('common.delete') }}
                </a-button>
              </a-space>
            </template>
          </template>
        </a-table>
      </a-card>
    </template>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import { message, Modal } from 'ant-design-vue'
import { useI18n } from 'vue-i18n'
import type { TableColumnsType } from 'ant-design-vue'
import type { Dayjs } from 'dayjs'
import {
  FileTextOutlined,
  ReloadOutlined,
  SearchOutlined,
  VideoCameraOutlined,
} from '@ant-design/icons-vue'

import type { ProjectListItem } from '@/modules/project/api'
import {
  aggregateWorkItemStatusCounts,
  getWorkItems,
  summarizeFolderStatusCounts,
  type WorkItemListResponse,
} from '@/modules/project/api/work-item'
import { useProjectI18n } from '@/modules/project/i18n/use-project-i18n'
import { useProjectStore } from '@/modules/project/stores'
import { formatTaskUserLabel } from '@/modules/task/utils/task-display'
import { runApiBatch } from '@/shared/api/use-api-batch'
import { useUserStore } from '@/stores'

const LIST_PAGE_SIZE_MAX = 20

const router = useRouter()
const { t } = useI18n()
const { tv } = useProjectI18n('list')
const projectStore = useProjectStore()
const userStore = useUserStore()

const loading = ref(true)
const pageError = ref(false)
const batchLoading = ref(false)
const aggregatesLoading = ref(false)
const selectedRowKeys = ref<number[]>([])
const searchKeyword = ref('')
const appliedKeyword = ref('')
const dateRange = ref<[Dayjs, Dayjs] | undefined>()
const appliedDateRange = ref<[Dayjs, Dayjs] | undefined>()
const currentPage = ref(1)
const pageSize = ref(LIST_PAGE_SIZE_MAX)
const aggregates = ref<Record<number, Record<string, number>>>({})

const storeProjects = computed(() => projectStore.projects)

const filteredProjects = computed(() => {
  let list = storeProjects.value
  const keyword = appliedKeyword.value.trim().toLowerCase()
  if (keyword) {
    list = list.filter((project) => project.title.toLowerCase().includes(keyword))
  }
  const range = appliedDateRange.value
  if (range && range.length === 2) {
    const start = range[0].startOf('day').toDate().getTime()
    const end = range[1].endOf('day').toDate().getTime()
    list = list.filter((project) => {
      if (!project.created_at) return false
      const created = new Date(project.created_at).getTime()
      return created >= start && created <= end
    })
  }
  return list
})

const pagedProjects = computed(() => {
  const size = Math.min(pageSize.value, LIST_PAGE_SIZE_MAX)
  const start = (currentPage.value - 1) * size
  return filteredProjects.value.slice(start, start + size)
})

const paginationConfig = computed(() => ({
  current: currentPage.value,
  pageSize: Math.min(pageSize.value, LIST_PAGE_SIZE_MAX),
  total: filteredProjects.value.length,
  showSizeChanger: true,
  pageSizeOptions: ['10', '20'],
  showQuickJumper: true,
  showTotal: (total: number, range: [number, number]) =>
    t('common.paginationTotal', { start: range[0], end: range[1], total }),
  onChange: (page: number, size: number) => {
    currentPage.value = page
    pageSize.value = Math.min(size, LIST_PAGE_SIZE_MAX)
  },
}))

const rowSelection = computed(() => ({
  selectedRowKeys: selectedRowKeys.value,
  onChange: (keys: (string | number)[]) => {
    selectedRowKeys.value = keys.map((key) => Number(key))
  },
}))

const columns = computed<TableColumnsType>(() => [
  {
    title: tv('colName'),
    dataIndex: 'title',
    key: 'title',
    ellipsis: true,
    width: 240,
  },
  {
    title: tv('colAggregate'),
    key: 'aggregate',
    width: 360,
  },
  {
    title: tv('colCreator'),
    key: 'creator',
    width: 140,
  },
  {
    title: tv('colCreatedAt'),
    key: 'createdAt',
    width: 160,
  },
  {
    title: tv('colAction'),
    key: 'action',
    width: 160,
    fixed: 'right',
  },
])

function customRow(record: ProjectListItem) {
  return {
    style: { cursor: 'pointer' },
    onClick: (event: MouseEvent) => {
      const target = event.target as HTMLElement | null
      if (target?.closest('button, a, .ant-checkbox-wrapper, .ant-dropdown')) return
      goToDetail(record)
    },
  }
}

function creatorLabel(record: ProjectListItem): string {
  return formatTaskUserLabel(record.creator, record.creator_id)
}

function formatDate(value?: string | null): string {
  if (!value) return t('common.none')
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value
  return date.toLocaleDateString()
}

function formatAggregate(projectId: number): string {
  const counts = aggregates.value[projectId]
  if (!counts) return tv('aggregateUnavailable')
  const summary = summarizeFolderStatusCounts(counts)
  return tv('aggregateSummary', {
    total: summary.total,
    completed: summary.completed,
    submitted: summary.submitted,
    processing: summary.processing,
  })
}

function goToDetail(record: ProjectListItem) {
  router.push({ name: 'ProjectDetail', params: { id: String(record.id) } })
}

function goToVideoUpload() {
  router.push('/video/upload')
}

function goToDocumentUpload() {
  router.push('/doc-workflow/upload')
}

function canDeleteProject(record: ProjectListItem): boolean {
  const userId = userStore.userInfo?.id
  return Boolean(userId && (userStore.isAdmin || record.creator_id === userId))
}

function handleSearch() {
  appliedKeyword.value = searchKeyword.value
  appliedDateRange.value = dateRange.value
  currentPage.value = 1
}

function resetFilters() {
  searchKeyword.value = ''
  dateRange.value = undefined
  appliedKeyword.value = ''
  appliedDateRange.value = undefined
  currentPage.value = 1
}

function clearSelection() {
  selectedRowKeys.value = []
}

async function fetchProjects() {
  loading.value = true
  pageError.value = false
  try {
    await projectStore.fetchProjects()
  } catch (error) {
    console.error('Failed to fetch projects:', error)
    pageError.value = true
  } finally {
    loading.value = false
  }
}

async function refreshProjects() {
  projectStore.clearCache()
  clearSelection()
  await fetchProjects()
  if (!pageError.value) {
    message.success(tv('refreshSuccess'))
  }
}

async function loadAggregates(projects: ProjectListItem[]) {
  if (projects.length === 0) {
    aggregates.value = {}
    return
  }
  aggregatesLoading.value = true
  try {
    const results = await runApiBatch<WorkItemListResponse>(
      projects.map((project) => (ctx) => getWorkItems(project.id, ctx.requestConfig)),
    )
    const next: Record<number, Record<string, number>> = {}
    results.forEach((result, index) => {
      const projectId = projects[index].id
      if (result.status === 'fulfilled') {
        next[projectId] = aggregateWorkItemStatusCounts(result.value.items ?? [])
      } else {
        next[projectId] = {}
      }
    })
    aggregates.value = next
  } catch (error) {
    console.error('Failed to load work item aggregates:', error)
  } finally {
    aggregatesLoading.value = false
  }
}

function projectItemTotal(projectId: number): number | null {
  const counts = aggregates.value[projectId]
  if (!counts) return null
  return summarizeFolderStatusCounts(counts).total
}

function deleteProject(record: ProjectListItem) {
  const itemTotal = projectItemTotal(record.id)
  if (itemTotal !== null && itemTotal > 0) {
    message.warning(tv('deleteBlockedHasItems', { name: record.title, count: itemTotal }))
    return
  }
  Modal.confirm({
    title: tv('deleteConfirmTitle'),
    content: tv('deleteConfirmContent', { name: record.title }),
    okText: t('common.confirm'),
    cancelText: t('common.cancel'),
    okType: 'danger',
    onOk: async () => {
      try {
        await projectStore.deleteProject(record.id)
        message.success(tv('deleteSuccess'))
        await fetchProjects()
      } catch (error) {
        console.error('Failed to delete project:', error)
      }
    },
  })
}

function batchDelete() {
  if (selectedRowKeys.value.length === 0) {
    message.warning(tv('pleaseSelect'))
    return
  }
  const blockedIds = selectedRowKeys.value.filter((id) => {
    const total = projectItemTotal(Number(id))
    return total !== null && total > 0
  })
  const deletableIds = selectedRowKeys.value.filter((id) => !blockedIds.includes(id))
  if (blockedIds.length > 0) {
    message.warning(tv('batchDeleteBlockedHasItems', { count: blockedIds.length }))
  }
  if (deletableIds.length === 0) {
    return
  }
  Modal.confirm({
    title: tv('confirmBatchDeleteTitle'),
    content: tv('confirmBatchDeleteContent', { count: deletableIds.length }),
    okText: t('common.confirm'),
    cancelText: t('common.cancel'),
    okType: 'danger',
    onOk: async () => {
      try {
        batchLoading.value = true
        await projectStore.batchDeleteProjects(deletableIds.map(Number))
        message.success(tv('batchDeleteSuccess'))
        clearSelection()
        await fetchProjects()
      } catch (error) {
        console.error('Failed to batch delete projects:', error)
      } finally {
        batchLoading.value = false
      }
    },
  })
}

watch(
  () => pagedProjects.value.map((project) => project.id).join(','),
  () => {
    void loadAggregates(pagedProjects.value)
  },
  { immediate: true },
)

watch(filteredProjects, (list) => {
  const size = Math.min(pageSize.value, LIST_PAGE_SIZE_MAX)
  const maxPage = Math.max(1, Math.ceil(list.length / size) || 1)
  if (currentPage.value > maxPage) {
    currentPage.value = maxPage
  }
})

onMounted(() => {
  if (projectStore.pagination.pageSize > LIST_PAGE_SIZE_MAX) {
    projectStore.setPagination(1, LIST_PAGE_SIZE_MAX)
  }
  void fetchProjects()
})
</script>

<style lang="less" scoped>
.project-management {
  padding: 24px;
  background: #f5f5f5;
  min-height: 100vh;
}

.page-header {
  margin-bottom: 24px;
}

.page-title {
  margin: 0;
  font-size: 24px;
  font-weight: 600;
  color: #262626;
}

.filter-card {
  margin-bottom: 16px;

  :deep(.ant-card-body) {
    padding: 16px 24px;
  }
}

.filter-content {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 24px;
}

.filter-form {
  flex: 1;
}

.batch-actions {
  margin-bottom: 16px;

  .batch-content {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  .selected-info {
    color: #1890ff;
    font-size: 14px;
  }

  :deep(.ant-card-body) {
    padding: 12px 24px;
  }
}

.guide-card {
  margin-bottom: 24px;
  background: linear-gradient(135deg, #f0f8ff 0%, #e6f7ff 100%);
  border: 1px solid #91d5ff;

  .guide-content {
    padding: 16px 8px;
  }

  h3 {
    margin: 0 0 8px;
    color: #1890ff;
    font-size: 18px;
    font-weight: 600;
  }

  p {
    margin: 0 0 16px;
    color: #595959;
  }
}

@media (max-width: 768px) {
  .project-management {
    padding: 16px;
  }

  .filter-content {
    flex-direction: column;
  }
}
</style>
